import { useEffect, useRef, useState } from "react";
import { Sparkles, Send, Trash2, X, Bot } from "lucide-react";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, API } from "@/lib/api";
import { toast } from "sonner";

/**
 * Floating "Ask AI" button + chat drawer.
 * Powered by GPT 5.4 Mini via Universal Key (upgradeable to Claude Sonnet 4.6
 * with a one-line change in /app/backend/server.py: ASSISTANT_MODEL_*).
 */
export default function AskAI() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]); // {role, text}
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);

  const loadHistory = async () => {
    try {
      const { data } = await api.get("/assistant/history");
      setMessages(data.map((m) => ({ id: m.message_id, role: m.role, text: m.text })));
    } catch (err) {
      console.warn("[ask-ai] history load failed:", err?.message || err);
    }
  };

  useEffect(() => { if (open) loadHistory(); }, [open]);
  useEffect(() => { scrollRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages.length, streaming]);

  // Allow other components to open AskAI with a pre-filled prompt.
  useEffect(() => {
    const handler = (e) => {
      setOpen(true);
      const prompt = e.detail?.prompt;
      if (prompt) {
        setTimeout(() => send(prompt), 250);
      }
    };
    window.addEventListener("open-ask-ai", handler);
    return () => window.removeEventListener("open-ask-ai", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearChat = async () => {
    if (!confirm("Clear this conversation?")) return;
    await api.post("/assistant/clear");
    setMessages([]);
    toast.success("Conversation cleared");
  };

  const send = async (overrideText) => {
    const text = (overrideText ?? input).trim();
    if (!text || streaming) return;
    setInput("");
    const userId = `local_u_${Date.now()}`;
    const asstId = `local_a_${Date.now()}`;
    setMessages((m) => [
      ...m,
      { id: userId, role: "user", text },
      { id: asstId, role: "assistant", text: "" },
    ]);
    setStreaming(true);

    try {
      const resp = await fetch(`${API}/assistant/chat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text }),
      });
      if (!resp.ok || !resp.body) throw new Error(`HTTP ${resp.status}`);

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let done = false;

      while (!done) {
        const { value, done: rdDone } = await reader.read();
        if (rdDone) break;
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split("\n\n");
        buffer = events.pop() || "";
        for (const evt of events) {
          const line = evt.split("\n").find((l) => l.startsWith("data:"));
          if (!line) continue;
          const payload = line.slice(5).trim();
          if (!payload) continue;
          try {
            const j = JSON.parse(payload);
            if (j.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", text: copy[copy.length - 1].text + j.delta };
                return copy;
              });
            } else if (j.error) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", text: `⚠️ ${j.error}` };
                return copy;
              });
              done = true;
            } else if (j.done) {
              done = true;
            }
          } catch { /* skip malformed */ }
        }
      }
    } catch (e) {
      toast.error("Assistant unavailable");
      setMessages((m) => m.slice(0, -1)); // drop empty assistant placeholder
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      {/* Floating button */}
      <button
        data-testid="ask-ai-btn"
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-40 hairline bg-[#39FF14] text-black px-5 py-3 flex items-center gap-2 font-display font-black uppercase tracking-widest text-xs hover:bg-white hover:translate-y-[-2px] transition-all"
        style={{ boxShadow: "0 0 0 1px rgba(57,255,20,0.4), 0 0 32px -8px rgba(57,255,20,0.6)" }}
      >
        <Sparkles size={14} strokeWidth={3} /> Ask AI
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          data-testid="ask-ai-drawer"
          side="right"
          className="rounded-none bg-[#0A0A0A] hairline border-l-neutral-800 w-full sm:max-w-lg flex flex-col p-0"
        >
          <SheetHeader className="px-6 py-4 border-b border-neutral-900">
            <SheetTitle className="font-display uppercase tracking-widest text-sm flex items-center gap-2">
              <Bot size={16} className="text-[#39FF14]" /> Ask AI
            </SheetTitle>
            <SheetDescription className="text-xs text-neutral-500">
              GPT 5.4 Mini · knows your KPIs, template, and team.
              Ask for coaching, template drafts, or app help.
            </SheetDescription>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="ask-ai-messages">
            {messages.length === 0 && !streaming && (
              <div className="mt-6 space-y-3">
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 px-2">Try asking…</div>
                {[
                  "How am I doing this week?",
                  "Draft a KPI template for a Cold-Caller role",
                  "Which team member should I check in with first?",
                  "What does the Traffic Light system do?",
                ].map((s) => (
                  <button
                    key={s}
                    data-testid={`suggest-${s.slice(0,10)}`}
                    onClick={() => setInput(s)}
                    className="w-full text-left hairline bg-[#0F0F0F] hover:bg-[#141414] px-4 py-3 text-sm transition-colors"
                  >{s}</button>
                ))}
              </div>
            )}
            {messages.map((m, i) => (
              <div key={m.id || `idx-${i}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] px-3 py-2 hairline text-sm whitespace-pre-wrap break-words ${
                    m.role === "user"
                      ? "bg-[#39FF14] text-black border-[#39FF14]"
                      : "bg-[#0F0F0F] text-neutral-100"
                  }`}
                >
                  {m.text}
                  {m.role === "assistant" && streaming && i === messages.length - 1 && (
                    <span className="inline-block w-2 h-3 bg-[#39FF14] ml-0.5 animate-pulse align-middle" />
                  )}
                </div>
              </div>
            ))}
            <div ref={scrollRef} />
          </div>

          <div className="px-4 pt-3 pb-4 border-t border-neutral-900">
            <div className="flex gap-2">
              <Textarea
                data-testid="ask-ai-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
                }}
                placeholder="Ask the AI…"
                disabled={streaming}
                className="rounded-none bg-[#0A0A0A] hairline min-h-[52px] max-h-40 resize-none focus-visible:ring-1 focus-visible:ring-[#39FF14] focus-visible:border-[#39FF14] flex-1"
              />
              <Button
                data-testid="ask-ai-send"
                onClick={send}
                disabled={!input.trim() || streaming}
                className="rounded-none bg-[#39FF14] text-black hover:bg-white disabled:opacity-40 h-auto px-4"
              >
                <Send size={16} />
              </Button>
            </div>
            <div className="flex items-center justify-between mt-2">
              <button
                data-testid="ask-ai-clear"
                onClick={clearChat}
                disabled={messages.length === 0}
                className="text-[10px] uppercase tracking-widest text-neutral-500 hover:text-red-400 disabled:opacity-30 flex items-center gap-1"
              >
                <Trash2 size={11} /> Clear conversation
              </button>
              <div className="text-[9px] uppercase tracking-widest text-neutral-600">
                GPT 5.4 Mini
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
